import { useState, useEffect, useContext } from "react";

import {
  TextField,
  Button,
  List,
  ListItem,
  ListItemText,
  Typography,
  Box,
  FormControlLabel,
  Switch,
  CircularProgress,
  Select,
  MenuItem,
  IconButton,
  Menu,
} from "@mui/material";

import MoreVertIcon from "@mui/icons-material/MoreVert";

import { EventContext } from "../../contexts/EventContext";
import request from "../../utilities/requests";
import formatDate from "../../utilities/formatDate";
import { toast } from "../../utilities/SnackBarUtils";

const channelLogos = {
  tnfm: "https://d19w6ranc5yet4.cloudfront.net/tnfm/tnfm-logo.png",
  eventbrite: "https://d19w6ranc5yet4.cloudfront.net/tnfm/eb-logo.png",
};

const Orders = () => {
  const { userData } = useContext(EventContext);
  const [orders, setOrders] = useState([]);
  const [query, setQuery] = useState("");
  const [all, setAll] = useState(false);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [eventId, setEventId] = useState("");
  const [menuAnchorEls, setMenuAnchorEls] = useState({});
  const handleMenuClick = (event, orderId) => {
    setMenuAnchorEls((prev) => ({ ...prev, [orderId]: event.currentTarget }));
  };

  useEffect(() => {
    if (userData) {
      // getOrders();
      getPairingEvents();
    }
  }, [userData]);

  useEffect(() => {
    if (query === "" && eventId === "") {
      getOrders();
    }
  }, [query, eventId]);

  useEffect(() => {
    if (eventId) {
      getOrders();
    }
  }, [eventId]);

  const getOrders = async () => {
    setLoading(true);
    const body = { query, all };
    if (eventId) body.eventId = eventId;
    const result = await request("orders/search", "post", body);
    if (result.success) {
      setOrders(result.data);
    }
    setLoading(false);
  };

  const getPairingEvents = async () => {
    let url = "pairingevent";
    if (userData?.role === "superadmin") {
      let org = userData.org._id;
      url = url + `/?orgId=${org}`;
    }
    const result = await request(url);

    if (result.success) {
      setEvents(result.pairingEvents);
    }
  };

  const handleSearch = () => {
    getOrders();
  };

  const handleReset = () => {
    setQuery("");
    setEventId("");
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const handleMenuClose = (orderId) => {
    setMenuAnchorEls((prev) => ({ ...prev, [orderId]: null }));
  };

  const handleSendReceipt = async (orderId) => {
    handleMenuClose(orderId);

    const result = await request('orders/sendreceipt','post',{orderId});
    if (result.success) {
      toast.success('Receipt sent!')
    }
    
    
  };

  return (
    <Box
      sx={{
        padding: 2,
        height: "100vh",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {userData?.role === "superadmin" && (
        <Box sx={{ display: "flex", justifyContent: "flex-end", pr: 2, mb: 1 }}>
          <FormControlLabel
            control={
              <Switch
                checked={all}
                onChange={(e) => setAll(e.target.checked)}
                size="small"
              />
            }
            label="View all orgs"
          />
        </Box>
      )}
      <Box sx={{ display: "flex", gap: 2, mb: 2, pl: 2, pr: 2 }}>
        <TextField
          label="Search Orders by Email, Name, or Order ID"
          variant="outlined"
          fullWidth
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          size="small"
        />
        <Button
          variant="contained"
          onClick={handleSearch}
          size="small"
          disabled={loading}
        >
          {loading ? <CircularProgress size={16} /> : "Search"}
        </Button>
        <Button
          variant="outlined"
          onClick={handleReset}
          size="small"
          disabled={loading}
        >
          Reset
        </Button>
      </Box>
      <Box sx={{ px: 2, mb: 2 }}>
        <TextField
          label="Filter by Event"
          select
          fullWidth
          size="small"
          value={eventId}
          onChange={(e) => {
            setEventId(e.target.value);
            if (e.target.value) {
              setQuery("");
            }
          }}
        >
          <MenuItem value="">All Events</MenuItem>
          {events.map((e) => (
            <MenuItem key={e._id} value={e.event._id}>
              {e.event.name} | {new Date(e.event.date).toLocaleDateString()}
            </MenuItem>
          ))}
        </TextField>
      </Box>

      <Box sx={{ flexGrow: 1, overflowY: "auto" }}>
        {orders.length === 0 ? (
          <Typography variant="body1">No orders found.</Typography>
        ) : (
          <List>
            {orders.map((order) => (
              <ListItem key={order._id} divider>
                <Box
                  sx={{
                    width: "100%",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <ListItemText
                    primary={`${order.firstName} ${order.lastName} • ${order.email}`}
                    secondary={
                      <>
                        <Typography component="span" variant="body2">
                          {order.eventName} — {formatDate(order.eventDate)}
                        </Typography>
                        <br />
                        <Typography component="span" variant="caption">
                          {order.orderId} | ${order.amountTotal} |{" "}
                          {order.status}
                        </Typography>
                        {userData?.role === "superadmin" &&
                          all &&
                          order.org && (
                            <>
                              <br />
                              <Typography component="span" variant="caption">
                                Org: {order.org}
                              </Typography>
                            </>
                          )}
                      </>
                    }
                  />
                  {order.source === "tnfm" && (
                    <>
                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuClick(e, order._id)}
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                      <Menu
                        anchorEl={menuAnchorEls[order._id]}
                        open={Boolean(menuAnchorEls[order._id])}
                        onClose={() => handleMenuClose(order._id)}
                      >
                        <MenuItem
                          onClick={() => {
                            handleSendReceipt(order._id);
                            // Add send receipt action here
                          }}
                        >
                          Send receipt
                        </MenuItem>
                      </Menu>
                    </>
                  )}
                  <Box
                    component="img"
                    src={channelLogos[order.source]}
                    alt={`${order.source} logo`}
                    sx={{
                      height: 16,
                      width: "auto",
                      objectFit: "contain",
                      display: "block",
                      maxWidth: "100%",
                      ml: "10px",
                    }}
                  />
                </Box>
              </ListItem>
            ))}
          </List>
        )}
      </Box>
    </Box>
  );
};

export default Orders;
